import React, { useState, useEffect, useCallback, memo } from 'react';
import { ArrowRight, ChevronLeft, ChevronRight, Pause, Play, RefreshCw } from 'lucide-react';
import useEmblaCarousel from 'embla-carousel-react';
import { Button } from './ui/button';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { apiService, HighlightEvent, UpcomingEvent, ApiService } from '../services/api';
import { SEMANA_CAFE, resolveFeaturedEventIds } from '../config/featuredEvents';
import { getEventPresentation } from '../lib/eventPresentation';

function EnhancedHeroComponent({ embedded = false }: { embedded?: boolean }) {
  const [events, setEvents] = useState<HighlightEvent[]>([]);
  const [details, setDetails] = useState<Record<string, UpcomingEvent>>({});
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [hovered, setHovered] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [firstArtworkLoaded, setFirstArtworkLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [carouselRef, carousel] = useEmblaCarousel({ loop: true, align: 'start', duration: reducedMotion ? 0 : 25 });

  useEffect(() => {
    // Let phone visitors choose when to advance while reading or scrolling.
    if (window.matchMedia('(max-width: 639px)').matches) setIsAutoPlaying(false);
  }, []);

  useEffect(() => {
    if (!carousel) return;
    const select = () => {
      const index = carousel.selectedScrollSnap();
      const focused = document.activeElement;
      const nextSlide = carousel.slideNodes()[index];
      if (focused instanceof HTMLAnchorElement && carousel.rootNode().contains(focused) && focused !== nextSlide) {
        focused.blur();
        window.requestAnimationFrame(() => { if (nextSlide?.isConnected) nextSlide.focus({ preventScroll: true }); });
      }
      setCurrentSlide(index);
    };
    const pause = () => setIsAutoPlaying(false);
    select();
    carousel.on('select', select).on('reInit', select).on('pointerDown', pause);
    return () => { carousel.off('select', select).off('reInit', select).off('pointerDown', pause); };
  }, [carousel]);

  useEffect(() => {
    if (!carousel) return;
    const surfaces = carousel.slideNodes().map((slide) => slide.querySelector<HTMLElement>('.home-hero__surface'));
    const updateDepth = () => {
      const left = carousel.rootNode().getBoundingClientRect().left;
      carousel.slideNodes().forEach((slide, index) => {
        const rect = slide.getBoundingClientRect();
        const distance = Math.min(1, Math.abs(rect.left - left) / Math.max(1, rect.width));
        surfaces[index]?.style.setProperty('--slide-scale', String(reducedMotion ? 1 : 1 - distance * .045));
      });
    };
    updateDepth();
    carousel.on('scroll', updateDepth).on('reInit', updateDepth);
    return () => { carousel.off('scroll', updateDepth).off('reInit', updateDepth); };
  }, [carousel, reducedMotion]);

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReducedMotion(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setFirstArtworkLoaded(false);
    setError(false);
    const timeout = window.setTimeout(() => {
      cancelled = true;
      setError(true);
      setIsLoading(false);
    }, 8000);
    Promise.all([
      apiService.getHighlightEvents(),
      apiService.getUpcomingEventsShared().catch(() => ({ events: [] })),
    ]).then(([highlights, upcoming]) => {
      if (cancelled) return;
      const byId: Record<string, UpcomingEvent> = {};
      upcoming.events.forEach((event) => { byId[String(event.eventId)] = event; });
      const featuredIds = resolveFeaturedEventIds(upcoming.events, import.meta.env);
      const rank = (event: HighlightEvent) => {
        const index = featuredIds.indexOf(ApiService.extractEventIdFromUrl(event.eventUrl));
        return index < 0 ? featuredIds.length : index;
      };
      const valid = highlights.events.filter((event) => event.eventUrl && event.imageUrl)
        .sort((a, b) => rank(a) - rank(b));
      setDetails(byId);
      setEvents(valid);
      setCurrentSlide(0);
      setError(valid.length === 0);
    }).catch(() => { if (!cancelled) setError(true); })
      .finally(() => { window.clearTimeout(timeout); if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; window.clearTimeout(timeout); };
  }, [attempt]);

  useEffect(() => {
    if (!carousel || !isAutoPlaying || hovered || reducedMotion || events.length < 2) return;
    const interval = window.setInterval(() => {
      if (!document.hidden) carousel.scrollNext();
    }, 7000);
    return () => window.clearInterval(interval);
  }, [carousel, isAutoPlaying, hovered, reducedMotion, events.length]);

  const goToSlide = useCallback((index: number) => {
    if (!events.length) return;
    carousel?.scrollTo((index + events.length) % events.length, reducedMotion);
    setIsAutoPlaying(false);
  }, [carousel, events.length, reducedMotion]);

  return (
    <section className={`home-hero container-modern${embedded ? '' : ' home-hero--standalone'}`}
      aria-label="Eventos em destaque" aria-roledescription="carrossel" data-single={events.length === 1}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      onFocusCapture={(event) => {
        if (!(event.target as HTMLElement).closest('[data-playback-control]')) setIsAutoPlaying(false);
      }}
      onKeyDown={(e) => {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          e.preventDefault();
          goToSlide(currentSlide + (e.key === 'ArrowLeft' ? -1 : 1));
        }
      }}>
      <div className="home-hero__heading"><span className="home-hero__section-label"><span aria-hidden="true" />Em destaque</span></div>
      {isLoading ? <div className="home-hero__placeholder" role="status">
          <span className="sr-only">Carregando destaques…</span>
          <div className="home-hero__skeleton" aria-hidden="true">
            <div className="home-hero__skeleton-art"><span className="home-hero__loader"><i /><i /><i /></span></div>
            <div className="home-hero__skeleton-copy">
              <span className="home-hero__skeleton-eyebrow" />
              <span className="home-hero__skeleton-title" />
              <span className="home-hero__skeleton-meta" />
              <span className="home-hero__skeleton-cta" />
            </div>
          </div>
          <div className="home-hero__skeleton-controls" aria-hidden="true"><span /><span /><span /></div>
        </div>
        : error ? <div className="home-hero__loading" role="status">
          <p>Não foi possível carregar os destaques.</p>
          <Button className="unique-button" onClick={() => setAttempt((value) => value + 1)}><RefreshCw size={16} /> Tentar novamente</Button>
        </div> : <div className="home-hero__arrival">
          <div ref={carouselRef} className="home-hero__stage">
            <div className="home-hero__track">
            {events.map((event, index) => {
              const eventId = ApiService.extractEventIdFromUrl(event.eventUrl);
              const detail = details[eventId];
              const editorial = SEMANA_CAFE.events[eventId];
              const { name: title, venue, date } = getEventPresentation({ ...detail, eventId,
                eventName: detail?.eventName || ApiService.extractEventNameFromUrl(event.eventUrl) });
              return <a key={event.eventUrl} href={event.eventUrl} target="_blank" rel="noopener" className="home-hero__slide"
                tabIndex={index === currentSlide ? 0 : -1} aria-hidden={index !== currentSlide}
                draggable={false} aria-label={`${title} — ${venue} — ${date} — ${editorial?.cta || 'Ver ingressos'}`}>
                <div className="home-hero__surface">
                <div className="home-hero__art" data-intro-ready={index === 0 && firstArtworkLoaded ? 'true' : undefined}>
                  <ImageWithFallback src={event.imageUrl} alt={`Arte de ${title}`} loading={index === currentSlide ? 'eager' : 'lazy'} className="home-hero__image" fallbackClassName="home-hero__image-frame"
                    onLoad={index === 0 ? () => setFirstArtworkLoaded(true) : undefined} />
                </div>
                <div className="home-hero__copy">
                  <span className="home-hero__eyebrow">{editorial ? 'Semana Café · Maceió' : 'Na agenda Unique'}</span>
                  <h2 className="home-hero__title">{title}</h2>
                  {editorial && <p className="home-hero__lineup">{editorial.lineup.join(' · ')}</p>}
                  <div className="home-hero__meta">
                    <span>{date}</span>
                    {venue && <span>{venue}</span>}
                  </div>
                  <span className="unique-button home-hero__cta">{editorial?.cta || 'Ver ingressos'} <ArrowRight size={17} aria-hidden="true" /></span>
                </div>
                </div>
              </a>;
            })}
            </div>
          </div>
          <span className="sr-only" role="status" aria-live={isAutoPlaying && !reducedMotion ? 'off' : 'polite'}>Destaque {currentSlide + 1} de {events.length}</span>
          {events.length > 1 && <div className="home-hero__controls">
            <div className="home-hero__dots">
              {events.map((event, index) => <button key={`${event.eventUrl}-${index}`}
                aria-label={`Ir para o destaque ${index + 1} de ${events.length}`}
                aria-current={index === currentSlide ? 'true' : undefined}
                onClick={() => goToSlide(index)}><span /></button>)}
            </div>
            <div className="home-hero__navigation">
              <span className="home-hero__count" aria-hidden="true">{String(currentSlide + 1).padStart(2, '0')} / {String(events.length).padStart(2, '0')}</span>
              {!reducedMotion && <button data-playback-control className="unique-icon-button" aria-label={isAutoPlaying ? 'Pausar carrossel' : 'Reproduzir carrossel'} onClick={() => setIsAutoPlaying((value) => !value)}>
                {isAutoPlaying ? <Pause size={15} /> : <Play size={15} />}
              </button>}
              <button className="unique-icon-button" onClick={() => goToSlide(currentSlide - 1)} aria-label="Evento anterior"><ChevronLeft size={19} /></button>
              <button className="unique-icon-button" onClick={() => goToSlide(currentSlide + 1)} aria-label="Próximo evento"><ChevronRight size={19} /></button>
            </div>
          </div>}
        </div>}
    </section>
  );
}

export const EnhancedHero = memo(EnhancedHeroComponent);
